import * as React from "react";
import type { ReactNode } from "react";
import * as ReactDOM from "react-dom";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { Braces, Copy, Check } from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useI18n } from "../../lib/i18n";
import * as localizationApi from "../../lib/api/localization";
import type {
  LocalizationCatalogPayload,
  LocalizationHealthReport,
} from "../../lib/api/localization";
import * as riStyles from "../../../ui/components/RealtimeIndicator/RealtimeIndicator.style.css";
import * as s from "./LocalizationHealthSection.css";

const RING_SIZE = 72;
const STROKE = 8;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const COLOR_TRANSLATED = "#16A34A";
const COLOR_UNTRANSLATED = "#FFFFFF";
const COLOR_UNLOCALIZED = "#DC2626";
const COLOR_ORPHANED = "#4B5563";

function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return (part / total) * 100;
}

/** One-decimal percentage label, e.g. "98.5%". */
function formatPct(part: number, total: number): string {
  return `${pct(part, total).toFixed(1)}%`;
}

function CatalogStatusBadge({
  catalogSource,
  generatedAt,
  onClick,
}: {
  catalogSource: "url" | "file";
  generatedAt: string | null;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{
    bottom: number;
    left: number;
  } | null>(null);
  const colors = riStyles.STATUS_COLORS.online;

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setTooltipVisible(true);
  }, []);
  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const generatedLabel = generatedAt
    ? (() => {
        try {
          return new Date(generatedAt).toLocaleString();
        } catch {
          return generatedAt;
        }
      })()
    : null;

  const tooltipEl =
    tooltipVisible && tooltipPos ? (
      <div
        className={riStyles.tooltipPortal}
        style={{
          position: "fixed",
          bottom: tooltipPos.bottom,
          left: tooltipPos.left,
          transform: "translateX(-50%)",
        }}
        role="tooltip"
      >
        <div className={riStyles.tooltipInner} style={{ position: "relative" }}>
          <span className={riStyles.tooltipTitle}>
            {t("pro.system.localization.health.catalogTooltipTitle")}
          </span>
          <span className={riStyles.tooltipRow}>
            {t("pro.system.localization.health.catalogSource", { source: catalogSource })}
          </span>
          {generatedLabel && (
            <span className={riStyles.tooltipRow}>
              {t("pro.system.localization.health.catalogGenerated", {
                date: generatedLabel,
              })}
            </span>
          )}
          <span className={riStyles.tooltipDescription}>
            {t("pro.system.localization.health.catalogClickHint")}
          </span>
          <div className={riStyles.tooltipCaret} />
        </div>
      </div>
    ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={riStyles.badge}
        style={{
          background: colors.background,
          color: colors.color,
          borderColor: colors.borderColor,
          cursor: "pointer",
        }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={() => {
          hideTooltip();
          onClick();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={t("pro.system.localization.health.catalogAria")}
      >
        <span className={riStyles.iconSlot}>
          <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true">
            <circle cx={4} cy={4} r={4} fill={colors.ring} />
          </svg>
        </span>
        {t("pro.system.localization.health.catalogBadge")}
      </span>
      {typeof document !== "undefined" &&
        ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}

function LanguageRing({
  translated,
  untranslated,
  unlocalized,
  orphaned,
  total,
}: {
  translated: number;
  untranslated: number;
  unlocalized: number;
  orphaned: number;
  total: number;
}) {
  const tLen = total > 0 ? (translated / total) * CIRCUMFERENCE : 0;
  const uLen = total > 0 ? (untranslated / total) * CIRCUMFERENCE : 0;
  const gLen = total > 0 ? (unlocalized / total) * CIRCUMFERENCE : 0;
  const oLen = total > 0 ? (orphaned / total) * CIRCUMFERENCE : 0;

  let offset = 0;
  const segments: Array<{ len: number; color: string }> = [
    { len: tLen, color: COLOR_TRANSLATED },
    { len: uLen, color: COLOR_UNTRANSLATED },
    { len: gLen, color: COLOR_UNLOCALIZED },
    { len: oLen, color: COLOR_ORPHANED },
  ];

  return (
    <div className={s.ringWrap}>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(156,163,175,0.25)"
          strokeWidth={STROKE}
        />
        {segments.map((seg, i) => {
          if (seg.len <= 0) return null;
          const dashoffset = -offset;
          offset += seg.len;
          return (
            <circle
              key={i}
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE}
              strokeDasharray={`${seg.len} ${CIRCUMFERENCE - seg.len}`}
              strokeDashoffset={dashoffset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              style={
                seg.color === COLOR_UNTRANSLATED
                  ? { filter: "drop-shadow(0 0 0.5px rgba(0,0,0,0.35))" }
                  : undefined
              }
            />
          );
        })}
      </svg>
      <span className={s.ringPct}>{formatPct(translated, total)}</span>
    </div>
  );
}

function CatalogJsonModal({
  open,
  setOpen,
  token,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  token: string;
}) {
  const { t } = useI18n();
  const [catalog, setCatalog] = React.useState<LocalizationCatalogPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    localizationApi
      .fetchLocalizationCatalog(token)
      .then((data) => {
        if (!cancelled) setCatalog(data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.message || t("pro.system.localization.health.error.loadCatalog"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token, t]);

  const handleCopy = async () => {
    if (!catalog) return;
    await navigator.clipboard.writeText(JSON.stringify(catalog, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="620px" maxWidth="860px" maxHeight="80vh">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Braces size={18} style={{ color: "var(--color-fgMuted)" }} />
          <Text level="h3" weight="bold">
            {t("pro.system.localization.health.catalogModalTitle")}
          </Text>
        </div>
        <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
          {t("pro.system.localization.health.catalogModalDescription")}
        </Text>

        {loading && (
          <Text level="body-small" style={{ color: "#9CA3AF" }}>
            {t("shared.common.loading")}
          </Text>
        )}
        {error && <Callout variant="error">{error}</Callout>}
        {!loading && !error && catalog && (
          <div
            style={{
              borderRadius: 8,
              padding: "12px 16px",
              fontSize: 12,
              lineHeight: 1.6,
              overflowX: "auto",
              fontFamily: "monospace",
              maxHeight: "55vh",
              overflowY: "auto",
            }}
          >
            <JsonView
              data={catalog}
              style={darkStyles}
              shouldExpandNode={(level: number) => level < 2}
            />
          </div>
        )}

        {catalog && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconLeft={copied ? <Check strokeWidth={2.5} /> : <Copy />}
              onClick={() => void handleCopy()}
            >
              {copied
                ? t("pro.system.localization.health.copied")
                : t("pro.system.localization.health.copyJson")}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function LocalizationHealthSection({
  hideChrome = false,
  onCatalogBadge,
  refreshKey = 0,
}: {
  hideChrome?: boolean;
  onCatalogBadge?: (node: ReactNode | null) => void;
  /** Bump after language import/create so health reloads even if WS is quiet. */
  refreshKey?: number;
} = {}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? getAuthCookie("access_token");
  const { subscribe, unsubscribe } = useWebSocket();

  const [report, setReport] = React.useState<LocalizationHealthReport | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [catalogOpen, setCatalogOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      // Always bypass server health cache when refreshKey bumps (e.g. after import).
      const data = await localizationApi.fetchLocalizationHealth(token, {
        fresh: refreshKey > 0,
      });
      setReport(data);
      setError(null);
    } catch (err: any) {
      setError(err?.message || t("pro.system.localization.health.error.load"));
    } finally {
      setLoading(false);
    }
  }, [token, t, refreshKey]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    let langReloadTimer: ReturnType<typeof setTimeout> | null = null;

    const applyReport = (data: LocalizationHealthReport) => {
      setReport(data);
      setError(null);
      setLoading(false);
    };

    const reloadFresh = () => {
      if (!token) return;
      void localizationApi
        .fetchLocalizationHealth(token, { fresh: true })
        .then(applyReport)
        .catch(() => {
          void load();
        });
    };

    const handler = (msg: { channel?: string; payload?: unknown }) => {
      const channel = msg.channel ?? "";
      if (channel === "entity:localization:health") {
        const payload = msg.payload as
          | LocalizationHealthReport
          | { invalidated?: boolean }
          | null;
        if (payload && typeof payload === "object" && "totals" in payload) {
          applyReport(payload as LocalizationHealthReport);
          return;
        }
        reloadFresh();
        return;
      }
      // Language lifecycle fires before module defs are written during import.
      // Debounce so we pick up the post-import health recompute, not an empty pack.
      if (channel === "entity:localization:languages") {
        if (langReloadTimer) clearTimeout(langReloadTimer);
        langReloadTimer = setTimeout(reloadFresh, 500);
      }
    };
    subscribe(
      ["entity:localization:health", "entity:localization:languages"],
      handler,
    );
    return () => {
      if (langReloadTimer) clearTimeout(langReloadTimer);
      unsubscribe(
        ["entity:localization:health", "entity:localization:languages"],
        handler,
      );
    };
  }, [subscribe, unsubscribe, load, token]);

  React.useEffect(() => {
    if (!onCatalogBadge) return;
    if (!report || report.catalogSource === "none") {
      onCatalogBadge(null);
      return;
    }
    const source = report.catalogSource;
    const generatedAt = report.generatedAt;
    onCatalogBadge(
      <CatalogStatusBadge
        catalogSource={source}
        generatedAt={generatedAt}
        onClick={() => setCatalogOpen(true)}
      />,
    );
  }, [onCatalogBadge, report?.catalogSource, report?.generatedAt]);

  const openLanguageDefinitions = (code: string) => {
    navigate({
      to: "/system/localization/definitions",
      search: { lang: code },
    });
  };

  if (!token) return null;

  if (loading && !report) {
    if (hideChrome) {
      return (
        <Text level="body-small" style={{ color: "#9CA3AF" }}>
          {t("shared.common.loading")}
        </Text>
      );
    }
    return (
      <div className={s.section}>
        <Text level="h3" weight="bold">
          {t("pro.system.localization.health.title")}
        </Text>
        <Text level="body-small" style={{ color: "#9CA3AF" }}>
          {t("shared.common.loading")}
        </Text>
      </div>
    );
  }

  if (error && !report) {
    if (hideChrome) {
      return <Callout variant="error">{error}</Callout>;
    }
    return (
      <div className={s.section}>
        <Text level="h3" weight="bold">
          {t("pro.system.localization.health.title")}
        </Text>
        <Callout variant="error">{error}</Callout>
      </div>
    );
  }

  if (!report) return null;

  const { totals } = report;
  const orphanedContentKeyCount = totals.orphanedContentKeyCount ?? 0;
  const total = Math.max(totals.total, 1);
  const translatedPct = pct(totals.fullyTranslatedCount, total);
  const untranslatedPct = pct(totals.partiallyTranslatedCount, total);
  const unlocalizedPct = pct(totals.unlocalizedCount, total);
  const orphanedPct = pct(orphanedContentKeyCount, total);

  const catalogBadgeNode =
    report.catalogSource === "none" ? null : (
      <CatalogStatusBadge
        catalogSource={report.catalogSource}
        generatedAt={report.generatedAt}
        onClick={() => setCatalogOpen(true)}
      />
    );

  const bodyContent = (
    <>

      {report.catalogSource === "none" && (
        <Callout variant="info">
          {t("pro.system.localization.health.noCatalogHint")}
        </Callout>
      )}

      <div
        className={s.barTrack}
        role="img"
        aria-label={t("pro.system.localization.health.bar.aria", {
          translated: totals.fullyTranslatedCount,
          untranslated: totals.partiallyTranslatedCount,
          unlocalized: totals.unlocalizedCount,
          orphaned: orphanedContentKeyCount,
          total: totals.total,
        })}
      >
        {totals.fullyTranslatedCount > 0 ? (
          <div
            className={s.barSegTranslated}
            style={{
              flex:
                totals.partiallyTranslatedCount === 0 &&
                totals.unlocalizedCount === 0 &&
                orphanedContentKeyCount === 0
                  ? "1 1 0"
                  : `${translatedPct} 0 0`,
            }}
            title={t("pro.system.localization.health.bar.translated", {
              count: totals.fullyTranslatedCount,
            })}
          />
        ) : null}
        {totals.partiallyTranslatedCount > 0 ? (
          <div
            className={s.barSegUntranslated}
            style={{ flex: `${untranslatedPct} 0 0` }}
            title={t("pro.system.localization.health.bar.untranslated", {
              count: totals.partiallyTranslatedCount,
            })}
          />
        ) : null}
        {totals.unlocalizedCount > 0 ? (
          <div
            className={s.barSegUnlocalized}
            style={{ flex: `${unlocalizedPct} 0 0` }}
            title={t("pro.system.localization.health.bar.unlocalized", {
              count: totals.unlocalizedCount,
            })}
          />
        ) : null}
        {orphanedContentKeyCount > 0 ? (
          <div
            className={s.barSegOrphaned}
            style={{ flex: `${orphanedPct} 0 0` }}
            title={t("pro.system.localization.health.bar.orphaned", {
              count: orphanedContentKeyCount,
            })}
          />
        ) : null}
        <span className={s.barPct} aria-hidden>
          {formatPct(totals.fullyTranslatedCount, totals.total)}
        </span>
      </div>

      <div className={s.legend}>
        <span className={s.legendItem}>
          <span className={clsx(s.swatch, s.swatchTranslated)} />
          {t("pro.system.localization.health.legend.translated", {
            count: totals.fullyTranslatedCount,
          })}
        </span>
        <span className={s.legendItem}>
          <span className={clsx(s.swatch, s.swatchUntranslated)} />
          {t("pro.system.localization.health.legend.untranslated", {
            count: totals.partiallyTranslatedCount,
          })}
        </span>
        <span className={s.legendItem}>
          <span className={clsx(s.swatch, s.swatchUnlocalized)} />
          {t("pro.system.localization.health.legend.unlocalized", {
            count: totals.unlocalizedCount,
          })}
        </span>
        {orphanedContentKeyCount > 0 ? (
          <span className={s.legendItem}>
            <span className={clsx(s.swatch, s.swatchOrphaned)} />
            {t("pro.system.localization.health.legend.orphaned", {
              count: orphanedContentKeyCount,
            })}
          </span>
        ) : null}
        <span className={s.legendItem}>
          {t("pro.system.localization.health.legend.total", { count: totals.total })}
          {" · "}
          {t("pro.system.localization.health.legend.localizedKeys", {
            count: totals.localizedKeyCount,
          })}
        </span>
      </div>

      {report.languages.length > 0 && (
        <div className={s.ringsRow}>
          {report.languages.map((lang) => {
            const orphaned = lang.orphanedCount ?? 0;
            const langTotal =
              lang.translatedCount +
              lang.untranslatedCount +
              lang.unlocalizedCount +
              orphaned;
            return (
              <button
                key={lang.code}
                type="button"
                className={s.ringCardButton}
                title={t("pro.system.localization.health.openDefinitions", {
                  name: lang.name,
                })}
                onClick={() => openLanguageDefinitions(lang.code)}
              >
                <LanguageRing
                  translated={lang.translatedCount}
                  untranslated={lang.untranslatedCount}
                  unlocalized={lang.unlocalizedCount}
                  orphaned={orphaned}
                  total={langTotal}
                />
                <div className={s.ringLabel}>
                  {lang.flagUrl ? (
                    <img
                      key={lang.flagUrl}
                      src={lang.flagUrl}
                      alt=""
                      className={s.flag}
                    />
                  ) : null}
                  {lang.name}
                </div>
                <div className={s.ringCounts}>
                  {lang.translatedCount} / {lang.untranslatedCount} /{" "}
                  {lang.unlocalizedCount}
                  <br />
                  <span style={{ opacity: 0.75 }}>{lang.code}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <CatalogJsonModal open={catalogOpen} setOpen={setCatalogOpen} token={token} />
    </>
  );

  if (hideChrome) {
    return bodyContent;
  }

  return (
    <div className={s.section}>
      <div className={s.sectionHeader}>
        <Text level="h3" weight="bold">
          {t("pro.system.localization.health.title")}
        </Text>
        {report.catalogSource === "none" ? (
          <span className={s.meta}>{t("pro.system.localization.health.noCatalog")}</span>
        ) : (
          catalogBadgeNode
        )}
      </div>
      {bodyContent}
    </div>
  );
}
